/**
 * Ассистент: панель, разговор и идущий ход.
 *
 * Стрим живёт здесь, а не в компоненте панели, и это главное решение файла.
 * Панель размонтируется при закрытии, а на переходе между задачей и доской
 * перерисовывается контент под ней; повесь запрос на жизненный цикл компонента
 * — и `onUnmounted` оборвал бы ответ на середине без единой ошибки на экране.
 * `AbortController` тоже здесь: остановить ход должно быть можно с любого
 * экрана, в том числе с закрытой панелью.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import {
  cancelTurn,
  createThread,
  emptyContext,
  listMessages,
  type AssistantContext,
  type ThreadMessage,
} from '@/features/assistant/api'
import {
  streamTurn,
  threadPath,
  type AssistantDoneReason,
  type AssistantEvent,
  type AssistantQuota,
} from '@/features/assistant/stream'
import { i18n } from '@/i18n'

const WIDTH_KEY = 'tracker-assistant-width'

/** Ширины панели из макета: 8-1 нарисован на 380, 8-3a — на узких 320. */
export const MIN_WIDTH = 320
export const MAX_WIDTH = 640
export const DEFAULT_WIDTH = 380

/** Ширины, к которым панель липнет при перетаскивании кромки. */
export const SNAP_WIDTHS = [360, 480, 640]

/** Насколько близко к снапу нужно подвести кромку, чтобы её притянуло. */
const SNAP_DISTANCE = 16

/**
 * Реплика на экране. Та же форма, что у прочитанных из базы: разницы между
 * «только что сказали» и «загрузили из истории» на экране нет, и вторая форма
 * означала бы две ветки рендера вместо одной.
 */
export type PanelMessage = ThreadMessage

export const clampWidth = (width: number): number =>
  Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(width)))

/** Ближайший снап, если кромка подведена к нему вплотную; иначе как есть. */
export const snapWidth = (width: number): number => {
  const target = SNAP_WIDTHS.find((snap) => Math.abs(snap - width) <= SNAP_DISTANCE)

  return clampWidth(target ?? width)
}

const readWidth = (): number => {
  if (typeof window === 'undefined') return DEFAULT_WIDTH

  const saved = Number(window.localStorage.getItem(WIDTH_KEY))

  return saved ? clampWidth(saved) : DEFAULT_WIDTH
}

export const useAssistantStore = defineStore('assistant', () => {
  const isOpen = ref(false)
  const width = ref(readWidth())

  const context = ref<AssistantContext>(emptyContext())
  const threadId = ref('')
  const messages = ref<PanelMessage[]>([])
  const quota = ref<AssistantQuota | null>(null)

  /**
   * Готовый текст ошибки, а не ключ перевода: причину отказа присылает
   * бэкенд («в этом разговоре уже идёт ответ»), и завернуть её в ключ нечем.
   * Свои сообщения берутся оттуда же, где чужие, — из `t`.
   */
  const error = ref('')

  /** Ход начат и ещё не закончился: композер показывает «Остановить». */
  const isStreaming = ref(false)

  /**
   * Пришла ли хоть одна порция текста. По этому признаку пропадает полоска
   * ожидания: между вопросом и первым словом модель ходит по инструментам, и
   * без полоски экран выглядит зависшим.
   */
  const hasText = ref(false)

  let controller: AbortController | null = null
  let nextId = 1

  const isEmpty = computed(() => messages.value.length === 0)

  const setWidth = (next: number) => {
    width.value = clampWidth(next)

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(WIDTH_KEY, String(width.value))
    }
  }

  const setContext = (next: AssistantContext) => {
    context.value = next
  }

  const open = () => {
    isOpen.value = true
  }

  /** Закрытие прячет панель, но не трогает ход: ответ дописывается в стор. */
  const close = () => {
    isOpen.value = false
  }

  const toggle = () => {
    isOpen.value = !isOpen.value
  }

  const appendText = (text: string) => {
    const last = messages.value[messages.value.length - 1]

    if (last?.role === 'assistant') last.text += text
    else messages.value.push({ id: nextId++, role: 'assistant', text })

    hasText.value = true
  }

  /**
   * Конец хода. Обрыв приезжает событием, а не исключением: статус ответа ушёл
   * с первой же строкой потока, и кодом ошибку сообщить уже нечем.
   */
  const finish = (reason?: AssistantDoneReason, message?: string) => {
    isStreaming.value = false

    if (reason === 'error' && message) error.value = message
  }

  const apply = (event: AssistantEvent) => {
    if (event.type === 'quota' && event.quota) quota.value = event.quota

    if (event.type === 'text' && event.text) appendText(event.text)

    if (event.type === 'done') finish(event.reason, event.message)
  }

  const send = async (text: string) => {
    const question = text.trim()

    if (!question || isStreaming.value) return

    error.value = ''
    hasText.value = false

    try {
      if (!threadId.value) threadId.value = await createThread(context.value)
    } catch {
      error.value = i18n.global.t('assistant.errors.thread')

      return
    }

    messages.value.push({ id: nextId++, role: 'user', text: question })

    controller = new AbortController()
    isStreaming.value = true

    try {
      await streamTurn(`${threadPath(threadId.value)}/messages`, {
        body: { text: question },
        signal: controller.signal,
        onEvent: apply,
        fallbackError: i18n.global.t('assistant.errors.stream'),
      })
    } catch (streamError) {
      // Отмену человек и так видит по остановленной панели — сообщать ему
      // о его же нажатии не о чем.
      if (!controller.signal.aborted) {
        error.value =
          streamError instanceof Error
            ? streamError.message
            : i18n.global.t('assistant.errors.stream')
      }
    } finally {
      controller = null
      isStreaming.value = false
    }
  }

  /**
   * Остановить ход. Сначала ручка на сервере, потом обрыв чтения: наоборот
   * запрос ушёл бы уже отменённым сигналом и не доехал бы вовсе.
   */
  const stop = async () => {
    if (!isStreaming.value) return

    const id = threadId.value

    controller?.abort()
    isStreaming.value = false

    if (!id) return

    try {
      await cancelTurn(id)
    } catch {
      // Ход всё равно перестал показываться; сервер добьёт его сам, когда
      // заметит закрытое соединение.
    }
  }

  /**
   * Новый разговор. Идущий ход при этом останавливается: его ответ приехал бы
   * в тред, которого на экране уже нет.
   */
  const reset = () => {
    void stop()

    threadId.value = ''
    messages.value = []
    error.value = ''
    hasText.value = false
  }

  /** Открыть прошлый разговор из истории. */
  const openThread = async (id: string) => {
    reset()
    threadId.value = id

    try {
      messages.value = await listMessages(id)
    } catch {
      error.value = i18n.global.t('assistant.errors.thread')
    }
  }

  return {
    isOpen,
    width,
    context,
    threadId,
    messages,
    quota,
    error,
    isStreaming,
    hasText,
    isEmpty,
    open,
    close,
    toggle,
    reset,
    openThread,
    setWidth,
    setContext,
    send,
    stop,
  }
})
